import {
  Button,
  Menu,
  Radio,
  RadioGroup,
  Text,
} from '@nike/eds';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
 SET_NPOS_FILTERS, setNposValue, setNposErrors, setNposFilters
} from '../../../../redux/actions';
import { getBodyTranslations } from '../../../../redux/selectors';
import { NPOS } from '../../../../utils/constants';
import { shouldShowCardNumberFilter } from '../../../../utils/ej-utils';
import { getFilterLabelText } from '../nposUtils';
import Filter from '../reusable/Filter';
import TextInput from '../reusable/TextInput/TextInput';

const CARD_INITIAL_STATE = { creditDebitCardNo: '', giftCardNo: '' };

const PaymentType = ({
 handleChange, storeLocale
}) => {
  const [open, setOpen] = useState(false);
  const [isValueSubmitted, setIsValueSubmitted] = useState(false);
  const [dirtyFlag, setDirtyFlag] = useState(false);

  const dispatch = useDispatch();

  const cardNo = useSelector((state) => state.nposFilters.cardNo);
  const paymentType = useSelector((state) => state.nposFilters.tenderType);
  const errors = useSelector((state) => state.nposErrors);
  const translations = useSelector(getBodyTranslations);

  const showCardNumberFilter = useMemo(() => shouldShowCardNumberFilter(paymentType, storeLocale), [paymentType, storeLocale]);

  const setCardNo = (value) => {
    setDirtyFlag(true);
    dispatch(setNposFilters({ cardNo: value }));
  };

  const setPaymentType = (value) => {
    setDirtyFlag(true);
    dispatch(setNposFilters({ tenderType: value }));
  };

  useEffect(() => {
    dispatch(setNposValue({ openFilter: open ? NPOS.FILTERS.PAYMENT_TYPE : null }));
  }, [open, dispatch]);

  useEffect(() => {
    const getError = (field, value) => {
      if ([NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD, NPOS.PAYMENT_TYPE.GIFT_CARD].includes(field)) {
        const isCreditDebitCard = field === NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD;
        return value && value.toString().length !== 4 ? translations[NPOS.ERRORS[isCreditDebitCard ? 'INVALID_CREDIT_DEBIT_CARD_NUMBER' : 'INVALID_GIFT_CARD_NUMBER']] : null;
      }
      return false;
    };

    dispatch(setNposErrors({
      [NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD]: getError(NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD, cardNo.creditDebitCardNo),
      [NPOS.PAYMENT_TYPE.GIFT_CARD]: getError(NPOS.PAYMENT_TYPE.GIFT_CARD, cardNo.giftCardNo),
    }));
  }, [paymentType, cardNo, translations, dispatch]);

  const onClearFilter = () => {
    setIsValueSubmitted(false);
    setPaymentType(null);
    setCardNo(CARD_INITIAL_STATE);
  };

  const validatePaymentType = () => {
    setIsValueSubmitted(true);
  };

  const onFilterCommit = () => {
    if (dirtyFlag) {
      validatePaymentType();

      handleChange('tenderType', paymentType);

      dispatch({ filter: { tenderType: paymentType }, type: SET_NPOS_FILTERS });

      if ([NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD, NPOS.PAYMENT_TYPE.GIFT_CARD].includes(paymentType)) {
        handleChange('cardNumber', paymentType === NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD ? cardNo.creditDebitCardNo : cardNo.giftCardNo);
      } else {
        handleChange('cardNumber', '');
      }
    }

    setOpen(false);
    return true;
  };

  const setValueSelected = (value) => {
    setCardNo(CARD_INITIAL_STATE);
    setPaymentType(value);
  };

  const onSetCardNo = (e) => {
    const value = e.target.value.replace(/\D/g, '');

    setCardNo({ ...CARD_INITIAL_STATE, [e.target.id]: value });
    e.stopPropagation();
    return true;
  };

  const Body = (
    <div
      className="p4-sm d-sm-flx flx-dir-sm-c flx-ai-sm-fs filter-body"
    >
      <div className="mb2-sm">
        <Text className="eds-type--subtitle-1 ta-sm-l">{translations.RL_PaymentType}</Text>
      </div>

      <div className="d-sm-flx flx-dir-sm-c" style={{ minWidth: '320px' }}>
        <RadioGroup
          name="radio-button-group"
          valueSelected={paymentType}
          onChange={(e) => setValueSelected(e.target.value)}
        >
          <Radio
            data-testid="credit-debit-card-radio"
            id="radio-1"
            label={translations.RL_CreditDebitCard}
            value={NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD}
          />
          {paymentType === NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD && showCardNumberFilter && (
            <div className="pl6-sm">
              <TextInput
                data-testid="credit-debit-card-no-input"
                defaultValue=""
                errorMessage={errors[NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD]}
                hasErrors={!!errors[NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD]}
                id="creditDebitCardNo"
                labelAlwaysVisible={false}
                maxChars={4}
                placeholder={translations.RL_CardNoPlaceholder}
                style={{ minWidth: '300px' }}
                type="number"
                value={cardNo.creditDebitCardNo}
                onChange={onSetCardNo}
              />
            </div>
          )}
          <Radio
            data-testid="cash-radio"
            id="radio-2"
            label={translations.UI_Cash}
            value={NPOS.PAYMENT_TYPE.CASH}
          />
          <Radio id="radio-3" label={translations.GiftCard} value={NPOS.PAYMENT_TYPE.GIFT_CARD} />
          {paymentType === NPOS.PAYMENT_TYPE.GIFT_CARD && (
          <div className="pl6-sm">
            <TextInput
              data-testid="gift-card-no-input"
              defaultValue=""
              errorMessage={errors[NPOS.PAYMENT_TYPE.GIFT_CARD]}
              hasErrors={!!errors[NPOS.PAYMENT_TYPE.GIFT_CARD]}
              id="giftCardNo"
              labelAlwaysVisible={false}
              maxChars={4}
              placeholder={translations.RL_GiftCardNoPlaceholder}
              style={{ minWidth: '300px' }}
              type="number"
              value={cardNo.giftCardNo}
              onChange={onSetCardNo}
            />
          </div>
          )}
          <Radio
            data-testid="complimentary-radio"
            id="radio-4"
            label={translations.RL_Complimentary}
            value={NPOS.PAYMENT_TYPE.COMPLIMENTARY}
          />
        </RadioGroup>
      </div>
    </div>
  );

  const Footer = ((
    <div className="d-sm-flx flx-jc-sm-fe p2-sm" style={{ gap: '20px' }}>
      {paymentType && (
        <Button className="pr2-sm" data-testid="payment-type-filter-clear-button" size="small" variant="ghost" onClick={onClearFilter}>
          {translations.RL_ClearFilter}
        </Button>
      )}
      <Button data-testid="payment-type-filter-done-button" size="small" variant="primary" onClick={onFilterCommit}>
        {translations.RL_Done}
      </Button>
    </div>
  ));

  const labelText = getFilterLabelText(NPOS.FILTERS.PAYMENT_TYPE, { isFilterApplied: !!paymentType, paymentType }, translations);

  const openFilterMenu = (value) => {
    setDirtyFlag(false);
    setOpen(value);
  };

  return (
    <Menu
      bodySlot={Body}
      className="npos-filter"
      footerSlot={Footer}
      isOpen={open}
      placement="bottom"
      onClickOutside={onFilterCommit}
    >
      <Filter
        filterId={NPOS.FILTERS.PAYMENT_TYPE}
        isError={isValueSubmitted && !!(errors[NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD] || errors[NPOS.PAYMENT_TYPE.GIFT_CARD])}
        isFilterApplied={!!paymentType}
        label={labelText}
        open={open}
        setOpen={openFilterMenu}
      />
    </Menu>
  );
};

PaymentType.propTypes = {
  handleChange: PropTypes.func.isRequired,
  storeLocale: PropTypes.string.isRequired,
};

export default PaymentType;
